// Updated AchievementCustomTable with Client Totals (no classname changes)
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    Card,
    CardBody,
    Table,
} from "reactstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { findTableColumnDefinitionByPreferenceKey } from "helpers/GeneralHelper";
import { useTableColumnPreferences } from "libs/context/TableColumnPreferencesContext";

const AchievementCustomTable = ({
    columns = [],
    data = [],
    isActionRequired = false,
    className = "",
    pageName = "IncentiveMonthlyDetails",
    tableKey: tableKeyProp = "",
}) => {
    const resolvedTableKey = String(tableKeyProp || pageName || "IncentiveMonthlyDetails").trim();
    const {
        visibleColumnOrder,
        handleColumnDragEnd,
    } = useTableColumnPreferences(resolvedTableKey, columns);

    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    const visibleColumnDefinitions = useMemo(
        () =>
            visibleColumnOrder
                .map((preferenceKey) =>
                    findTableColumnDefinitionByPreferenceKey(columns, preferenceKey)
                )
                .filter(Boolean),
        [columns, visibleColumnOrder]
    );

    const handleSort = (accessor) => {
        setSortConfig((prev) => {
            if (prev.key === accessor) {
                return {
                    key: accessor,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return { key: accessor, direction: "asc" };
        });
    };

    const sortedData = useMemo(() => {
        if (!sortConfig.key) return data;
        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            if (typeof aValue === "number" && typeof bValue === "number") {
                return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
            }

            return sortConfig.direction === "asc"
                ? String(aValue).localeCompare(String(bValue))
                : String(bValue).localeCompare(String(aValue));
        });
    }, [data, sortConfig]);

    const IconEdit = (
        <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 17.25V21h3.75l11-11.03-3.75-3.75L3 17.25zm17.71-10.04a1.003 1.003 0 0 0 0-1.42l-2.5-2.5a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.99-1.66z" />
        </svg>
    );

    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) return null;
        return sortConfig.direction === "asc" ? (
            <svg width="12" height="12" fill="currentColor"><path d="M7 14l5-5 5 5H7z" /></svg>
        ) : (
            <svg width="12" height="12" fill="currentColor"><path d="M7 10l5 5 5-5H7z" /></svg>
        );
    };

    const getItemStyle = (isDragging, style) => ({
        userSelect: "none",
        cursor: "grab",
        background: isDragging ? "#f1f5f9" : "transparent",
        boxShadow: isDragging ? "0 4px 8px rgba(0,0,0,0.15)" : "none",
        transition: "all 0.2s ease",
        ...style,
    });

    const headerColSpan = visibleColumnDefinitions.length + 1;
    const totalColumnAccessors = ["totalAmount", "incentive", "incentiveAmount"];
    const totalAccessor =
        totalColumnAccessors.find((key) =>
            visibleColumnDefinitions.some((col) => col?.accessor === key)
        ) || "incentive";
    const totalIndex = visibleColumnDefinitions.findIndex(
        (col) => col?.accessor === totalAccessor
    );

    return (
        <Card className={`shadow-sm border-0 ${className}`.trim()}>
            <CardBody className="p-0">
                <div className="table-responsive table-common-bg-color">
                    <Table hover borderless className="align-middle mb-0 custom-table">
                        <DragDropContext onDragEnd={handleColumnDragEnd}>
                            <Droppable droppableId="columns" direction="horizontal">
                                {(provided) => (
                                    <thead
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className="table-common-bg-color"
                                    >
                                        <tr className="common-table-row-stl custom-table-border-bottom">
                                            {visibleColumnDefinitions.map((col, index) => {
                                                const accessor = col?.accessor;
                                                return (
                                                    <Draggable
                                                        key={accessor}
                                                        draggableId={String(accessor)}
                                                        index={index}
                                                    >
                                                        {(dragProvided, snapshot) => (
                                                            <th
                                                                ref={dragProvided.innerRef}
                                                                {...dragProvided.draggableProps}
                                                                {...dragProvided.dragHandleProps}
                                                                onClick={() => handleSort(accessor)}
                                                                className="text-nowrap"
                                                                style={getItemStyle(
                                                                    snapshot.isDragging,
                                                                    dragProvided.draggableProps.style
                                                                )}
                                                            >
                                                                {col?.Header}{" "}
                                                                <SortIcon column={accessor} />
                                                            </th>
                                                        )}
                                                    </Draggable>
                                                );
                                            })}
                                            {provided.placeholder}
                                            <th />
                                        </tr>
                                    </thead>
                                )}
                            </Droppable>

                            <tbody className="common-table-body-stl">
                                {sortedData.map((teamObj, tIdx) => (
                                    <React.Fragment key={tIdx}>
                                        {(Array.isArray(teamObj?.members) ? teamObj.members : []).map(
                                            (memberObj, mIdx) => {
                                                const memberRows = Array.isArray(memberObj?.rows)
                                                    ? memberObj.rows
                                                    : [];
                                                const memberTotal = memberRows.reduce((sum, r) => {
                                                    const value =
                                                        r?.[totalAccessor] ??
                                                        r?.totalAmount ??
                                                        r?.incentive ??
                                                        r?.incentiveAmount ??
                                                        0;
                                                    return sum + (Number(value) || 0);
                                                }, 0);

                                                return (
                                                    <React.Fragment key={mIdx}>
                                                        <tr className="color-fafafa">
                                                            <td
                                                                colSpan={headerColSpan}
                                                                className="fw-semibold py-2"
                                                            >
                                                                {memberObj.name}
                                                            </td>
                                                        </tr>

                                                        {memberRows.map((row, rIdx) => (
                                                            <tr key={rIdx}>
                                                                {visibleColumnDefinitions.map((col) => (
                                                                    <td key={col?.accessor}>
                                                                        {row?.[col?.accessor] ?? "—"}
                                                                    </td>
                                                                ))}
                                                                <td className="text-center">
                                                                    {isActionRequired && (
                                                                        <span className="text-muted cursor-pointer">
                                                                            {IconEdit}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}

                                                        <tr>
                                                            {visibleColumnDefinitions.map((col, colIdx) => (
                                                                <td
                                                                    key={`total-${col?.accessor}`}
                                                                    className="fw-semibold py-2"
                                                                >
                                                                    {colIdx === totalIndex
                                                                        ? memberTotal
                                                                        : ""}
                                                                </td>
                                                            ))}
                                                            <td />
                                                        </tr>
                                                    </React.Fragment>
                                                );
                                            }
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </DragDropContext>
                    </Table>
                </div>
            </CardBody>
        </Card>
    );
};

AchievementCustomTable.propTypes = {
    columns: PropTypes.array,
    data: PropTypes.array,
    isActionRequired: PropTypes.bool,
    className: PropTypes.string,
    pageName: PropTypes.string,
    tableKey: PropTypes.string,
};

export default AchievementCustomTable;
